import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  CarePathwayStep,
  StepImpact,
} from '../../../../services/result-data/result-data.service';
import { CardComponent, DataPointCardConfig } from '../../card/card.component';
import { resolveCardTitle } from '../../card/card.utils';
import { DEFAULT_CARD_TITLE } from '../../../../constants';
import { MatIconModule } from '@angular/material/icon';
import { SelectedStepService } from '../../../../services/selected-step/selected-step.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { CarePathwayStepperComponent } from '../care-pathway-stepper/care-pathway-stepper.component';
import { TransitionService } from '../../../../services/transition/transition.service';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  selector: 'app-care-pathway-step',
  imports: [
    CardComponent,
    MatIconModule,
    MatCheckboxModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    CarePathwayStepperComponent,
  ],
  templateUrl: './care-pathway-step.component.html',
  styleUrl: './care-pathway-step.component.scss',
  animations: [
    // fade in from left
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(24px)' }),
        animate('0.3s ease-out', style({ opacity: 1, transform: 'none' })),
      ]),
    ]),
  ],
})
export class CarePathwayStepComponent implements OnChanges {
  @Input() stepIndex!: number;
  @Input() allSteps!: CarePathwayStep[];

  cardConfigs: DataPointCardConfig[] = [];
  filteredCardConfigs: DataPointCardConfig[] = [];
  cardCategories: { name: string; active: boolean }[] = [];

  constructor(
    private readonly _selectedStepService: SelectedStepService,
    private readonly _transitionService: TransitionService
  ) {}

  get step() {
    return this.allSteps[this.stepIndex];
  }

  ngOnChanges(changes: SimpleChanges): void {
    // when step changes OR when scenario updated
    if (changes['stepIndex'] || changes['allSteps']) {
      this._initializeCardCategories();
      this._generateCardConfigsFromStep();
      this._updateFilteredCardConfigs();
    }
  }

  onClickStep(stepIndex: number) {
    this._selectedStepService.selectStep(stepIndex);
  }

  onToggleFilterCheckbox() {
    this._transitionService.run(() => this._updateFilteredCardConfigs());
  }

  onClickReturnButton() {
    this._transitionService.run(() => this._selectedStepService.deselectStep());
  }

  onHideCard(cardIndex: number) {
    this._transitionService.run(() => {
      this.filteredCardConfigs.splice(cardIndex, 1);
    });
  }

  private _updateFilteredCardConfigs() {
    const active = new Set(
      this.cardCategories.filter((c) => c.active).map((c) => c.name)
    );
    this.filteredCardConfigs = this.cardConfigs.filter((c) =>
      active.has(c.categoryName)
    );
  }

  private _initializeCardCategories() {
    this.cardCategories = [];
    for (const cat of this.step.impactCategories) {
      if (this.cardCategories.some((c) => c.name === cat.name)) continue;
      this.cardCategories.push({ name: cat.name, active: true });
    }
  }

  private _generateCardConfigsFromStep() {
    this.cardConfigs = [];
    const colorByCat = new Map(this.cardCategories.map((c, i) => [c.name, i]));

    for (let catIndex = 0; catIndex < this.step.impactCategories.length; catIndex++) {
      const cat = this.step.impactCategories[catIndex];
      const catName = cat.name;
      const sanitizedCatName = catName.replace(/[^a-zA-Z0-9_-]/g, '_');
      for (let impactIndex = 0; impactIndex < cat.impacts.length; impactIndex++) {
        const impact = cat.impacts[impactIndex];
        const sanitizedImpactName = impact.name.replace(/[^a-zA-Z0-9_-]/g, '_');
        const data = impact.subImpacts.map((si) => ({
          label: si.name,
          value: si.value,
          unit: si.unit,
          addPieChart: si.addPieChart,
        }));
        this.cardConfigs.push({
          transitionId: `card-${this.stepIndex}-${sanitizedCatName}-${sanitizedImpactName}-${impactIndex}`,
          title: resolveCardTitle(impact.name, DEFAULT_CARD_TITLE),
          type: 'dataPoint',
          dataPointCardType:
            impact.subImpacts.length === 1 ? 'single' : 'double',
          imgFileName: impact.imageFileName,
          info: impact.description,
          categoryName: catName,
          data,
          colorIndex: colorByCat.get(catName) ?? 0,
          cardIndex: impact.cardIndex,
          accent: impact.cardAccent,
          colspan: this._resolveImpactColspan(impact),
        });
      }
    }

    this.cardConfigs.sort((a, b) => {
      const ai = a.cardIndex ?? Number.MAX_SAFE_INTEGER;
      const bi = b.cardIndex ?? Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
  }

  private _resolveImpactColspan(impact: StepImpact): number {
    if (impact.cardColspan && impact.cardColspan > 0) {
      return impact.cardColspan;
    }

    const hasImage = !!impact.imageFileName;
    const isDouble = impact.subImpacts.length > 1;

    if (isDouble) {
      return hasImage ? 4 : 6;
    }

    return hasImage ? 4 : 2;
  }
}
